import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

/**
 * directionsTargetSchema is the itinerary the turn-by-turn directions modal is
 * open for, plus an optional segment to narrow the sheet to a single segment
 * (null = the whole itinerary). Zod-first because the value is shared with the
 * directions-open tool input and validated when an agent opens the modal; a null
 * target (modal closed) lives outside the schema as the absence of a value.
 */
export const directionsTargetSchema = z.object({
  itineraryId: z.string(),
  segmentId: z.string().nullable(),
});

/**
 * DirectionsTarget is the itinerary (and optional segment) the directions modal
 * is open for, derived from directionsTargetSchema. The directions slot holds
 * this or null (the modal is closed).
 */
export type DirectionsTarget = z.infer<typeof directionsTargetSchema>;

/**
 * DirectionsSliceState is the open turn-by-turn directions modal slot.
 */
export interface DirectionsSliceState {
  /**
   * directionsTarget is the open directions modal state (null = the modal is
   * closed). It holds the itinerary the turn-by-turn sheet is generated for and
   * an optional segment to scope it; the generated PDF is built and downloaded in
   * the surface, never mirrored here. Set only through the tool bus, like every
   * other UiAction.
   */
  directionsTarget: DirectionsTarget | null;
}

/**
 * DirectionsSliceActions is the whole-slot setter that drives the directions
 * modal, reached only through the tool bus.
 */
export interface DirectionsSliceActions {
  /**
   * setDirections opens the directions modal with the given target, or closes it
   * when passed null. The whole-slot setter the open/close tools drive, mirroring
   * setShare — the modal keeps no narrower fields in the store.
   */
  setDirections: (target: DirectionsTarget | null) => void;
}

/**
 * directionsInitialState seeds the directions modal slot closed.
 */
export const directionsInitialState: DirectionsSliceState = {
  directionsTarget: null,
};

/**
 * createDirectionsSlice builds the directions modal action. The single
 * whole-slot setter opens the modal for an itinerary (optionally scoped to a
 * segment) or closes it, mirroring the share slice's open/close shape.
 */
export const createDirectionsSlice: StateCreator<
  DirectionsSliceState & DirectionsSliceActions,
  [],
  [],
  DirectionsSliceActions
> = (set) => ({
  setDirections: (directionsTarget) => set({ directionsTarget }),
});

/**
 * selectDirections is the open directions modal state, or null when the modal is
 * closed. The mount-guard selector the directions modal reads to decide whether
 * to render at all — narrow so the guard re-renders only on the open/close flip,
 * mirroring selectShare.
 */
export const selectDirections = (state: DirectionsSliceState): DirectionsTarget | null =>
  state.directionsTarget;
